'use client'

/**
 * MediaUploadMulti — multi-file PRIVATE uploader for sensitive proof (masjid-claim ownership/ID
 * documents). Each file is POSTed to /api/media/upload (which forces visibility=private_sensitive for
 * ownerType='claim'/'report'), creating one media_assets row keyed by owner_type + owner_id. Files are
 * NEVER given a public URL — reviewers open them through the gated /api/media/private/[id] route.
 *
 * Mirrors MasjidPhotosField's per-tile UX (uploading spinner / error+retry / remove) but on the private
 * media path. Accepts images AND documents (PDF/Word/Excel/text). The parent receives the successfully
 * uploaded assets via onChange; a half-finished or failed upload is never surfaced.
 */

import { useEffect, useRef, useState } from 'react'
import { FileText, ImageIcon, Plus, RotateCw, ShieldCheck, X } from 'lucide-react'

export type UploadedAsset = { id: string; originalName: string; mimeType: string; sizeBytes: number }

type ItemStatus = 'uploading' | 'done' | 'error'
type Item = { localId: number; name: string; mimeType: string; status: ItemStatus; asset?: UploadedAsset; error?: string; file: File }

interface Props {
  label?: string
  ownerType: 'claim' | 'report'
  ownerId: string
  masjidSlug?: string
  masjidId?: string
  purpose?: 'proof' | 'document'
  max?: number
  helper?: string
  onChange?: (assets: UploadedAsset[]) => void
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.txt'

function fileSize(size: number) {
  if (size > 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(size / 1024))} KB`
}
const isImage = (mime: string) => mime.startsWith('image/')

export default function MediaUploadMulti({
  label = 'Private proof documents', ownerType, ownerId, masjidSlug, masjidId, purpose = 'proof', max = 6, helper, onChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<Item[]>([])
  const nextId = useRef(1)

  // Surface only successfully-uploaded assets (never a pending/failed one) to the parent.
  useEffect(() => {
    onChange?.(items.filter((i) => i.status === 'done' && i.asset).map((i) => i.asset as UploadedAsset))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const setItem = (localId: number, patch: Partial<Item>) =>
    setItems((prev) => prev.map((i) => (i.localId === localId ? { ...i, ...patch } : i)))

  async function startUpload(it: Item) {
    try {
      const form = new FormData()
      form.append('file', it.file)
      form.append('ownerType', ownerType)
      form.append('ownerId', ownerId)
      if (masjidSlug) form.append('masjidSlug', masjidSlug)
      if (masjidId) form.append('masjidId', masjidId)
      form.append('visibility', 'private_sensitive') // claim/report are forced to this server-side anyway
      form.append('purpose', purpose)
      const res = await fetch('/api/media/upload', { method: 'POST', body: form })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.asset?.id) { setItem(it.localId, { status: 'error', error: data?.error || 'Upload failed.' }); return }
      setItem(it.localId, { status: 'done', asset: data.asset as UploadedAsset, error: undefined })
    } catch {
      setItem(it.localId, { status: 'error', error: 'Upload failed. Please try again.' })
    }
  }

  function addFiles(files: FileList | null) {
    if (!files || !ownerId) return
    const remaining = max - items.length
    const chosen = Array.from(files).slice(0, Math.max(0, remaining))
    if (!chosen.length) return
    const created: Item[] = chosen.map((f) => ({ localId: nextId.current++, name: f.name, mimeType: f.type, status: 'uploading', file: f }))
    setItems((prev) => [...prev, ...created])
    created.forEach((it) => void startUpload(it))
  }

  const remove = (localId: number) => setItems((prev) => prev.filter((i) => i.localId !== localId))
  const retry = (localId: number) => {
    const it = items.find((i) => i.localId === localId)
    if (!it) return
    setItem(localId, { status: 'uploading', error: undefined })
    void startUpload(it)
  }

  const full = items.length >= max
  const doneCount = items.filter((i) => i.status === 'done').length

  return (
    <div className="mm">
      <div className="mmHead">
        <span><ShieldCheck size={14} /> {label}</span>
        <span className="mmCount">{doneCount}/{max}</span>
      </div>

      <div className="mmGrid">
        {items.map((it) => (
          <div key={it.localId} className={`mmTile ${it.status === 'error' ? 'isErr' : ''}`}>
            <div className="mmIcon">{isImage(it.mimeType) ? <ImageIcon size={18} /> : <FileText size={18} />}</div>
            <div className="mmName" title={it.name}>{it.name}</div>
            <div className="mmMeta">
              {it.status === 'uploading' ? 'Uploading…' : it.status === 'error' ? (it.error || 'Failed') : fileSize(it.file.size)}
            </div>
            {it.status === 'error' && (
              <button type="button" className="mmRetry" onClick={() => retry(it.localId)}><RotateCw size={12} /> Retry</button>
            )}
            <button type="button" className="mmRemove" onClick={() => remove(it.localId)} aria-label="Remove file"><X size={12} /></button>
          </div>
        ))}
        {!full && (
          <button type="button" className="mmAdd" disabled={!ownerId} onClick={() => inputRef.current?.click()}>
            <Plus size={20} />
            <small>Add proof</small>
          </button>
        )}
      </div>

      <p className="mmHelper">{helper || 'Private — visible only to reviewers, never public. Images, PDF, Word, Excel, or text. Max 15MB each.'}</p>

      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { addFiles(e.target.files); if (inputRef.current) inputRef.current.value = '' }} />

      <style jsx>{`
        .mm { display: grid; gap: 8px; min-width: 0; }
        .mmHead { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .mmHead span:first-child { display: inline-flex; align-items: center; gap: 7px; color: #64748b; font-size: 11px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; }
        .mmCount { color: #64748b; font-size: 11px; font-weight: 800; }
        .mmGrid { display: flex; flex-wrap: wrap; gap: 10px; }
        .mmTile { position: relative; width: 118px; min-height: 96px; border: 1px solid #d7e0dc; border-radius: 12px; background: #fbfdfa; padding: 10px; display: grid; gap: 3px; align-content: start; }
        .mmTile.isErr { border-color: #fecaca; background: #fff7f7; }
        .mmIcon { color: #0e7c61; }
        .mmName { font-size: 12px; font-weight: 700; color: #12312d; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .mmMeta { font-size: 10.5px; font-weight: 700; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .mmTile.isErr .mmMeta { color: #b42318; }
        .mmRetry { margin-top: 4px; display: inline-flex; align-items: center; gap: 4px; border: 1px solid #d7e0dc; border-radius: 8px; background: #fff; color: #b42318; font-size: 11px; font-weight: 800; padding: 3px 8px; cursor: pointer; }
        .mmRemove { position: absolute; top: 5px; right: 5px; width: 22px; height: 22px; display: grid; place-items: center; border: none; border-radius: 999px; background: rgba(0,0,0,.55); color: #fff; cursor: pointer; }
        .mmAdd { width: 118px; min-height: 96px; display: grid; place-items: center; gap: 4px; border: 1px dashed #b9cbc4; border-radius: 12px; background: linear-gradient(145deg, #fff, #fbfdfa); color: #0e7c61; cursor: pointer; }
        .mmAdd:disabled { opacity: .6; cursor: not-allowed; }
        .mmAdd small { font-size: 11px; font-weight: 800; }
        .mmHelper { margin: 0; color: #64748b; font-size: 11px; font-weight: 700; line-height: 1.45; }
      `}</style>
    </div>
  )
}
